import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Shield, Award, Clock, Users, 
  Hammer, HardHat, Ruler, Home, Wrench, Paintbrush
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from '../components/common/SEOHead';
import UKMap from '../components/common/UKMap';

// User provided images
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_b8d07c5d-fcca-4dc8-ad54-fc1da9664427/artifacts/scoo2t6q_IMAGENS%20PARA%20SITE8.png';
const WORK_IMAGE_1 = 'https://customer-assets.emergentagent.com/job_b8d07c5d-fcca-4dc8-ad54-fc1da9664427/artifacts/znraqcu0_IMAGENS%20PARA%20SITE7.png';
const WORK_IMAGE_2 = 'https://customer-assets.emergentagent.com/job_b8d07c5d-fcca-4dc8-ad54-fc1da9664427/artifacts/atrvqanj_IMAGENS%20PARA%20SITE1.png';
const WORK_IMAGE_3 = 'https://customer-assets.emergentagent.com/job_fort-build-uk/artifacts/phfmtmuf_IMAGENS%20PARA%20SITE4.png';
const WORK_IMAGE_4 = 'https://customer-assets.emergentagent.com/job_fort-build-uk/artifacts/lm4nc845_IMAGENS%20PARA%20SITE5.png';
const WORK_IMAGE_5 = 'https://customer-assets.emergentagent.com/job_fort-build-uk/artifacts/0x5tp7x0_IMAGENS%20PARA%20SITE6.png';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const HomePage = () => {
  const { t } = useLanguage();

  const services = [
    { icon: Hammer, title: t('services.construction.items.demolition.title'), link: '/services' },
    { icon: HardHat, title: t('services.construction.items.roofing.title'), link: '/services' },
    { icon: Ruler, title: t('services.construction.items.extensions.title'), link: '/services' },
    { icon: Home, title: t('services.renovation.items.loft.title'), link: '/services' },
    { icon: Wrench, title: t('services.construction.items.plumbing.title'), link: '/services' },
    { icon: Paintbrush, title: t('services.renovation.items.painting.title'), link: '/services' },
  ];

  const whyUsItems = [
    { icon: Shield, title: t('whyUs.items.safety.title'), desc: t('whyUs.items.safety.desc') },
    { icon: Award, title: t('whyUs.items.quality.title'), desc: t('whyUs.items.quality.desc') },
    { icon: Users, title: t('whyUs.items.experience.title'), desc: t('whyUs.items.experience.desc') },
    { icon: Clock, title: t('whyUs.items.reliable.title'), desc: t('whyUs.items.reliable.desc') },
  ];

  return (
    <>
      <SEOHead 
        title={t('meta.home.title')}
        description={t('meta.home.description')}
      />

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center pt-20"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE}
            alt="Construction worker welding"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/95 via-navy-deep/80 to-navy-deep/40" />
        </div>

        <div className="container-main relative z-10 py-20">
          <div className="max-w-3xl">
            <motion.h1 
              className="font-alatsi text-5xl md:text-7xl text-white leading-tight mb-8"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              data-testid="hero-title"
            >
              {t('hero.title')}
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-gray-200 leading-relaxed mb-10 max-w-2xl"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Link
                to="/get-in-touch"
                className="inline-flex items-center space-x-3 bg-gold-main text-white hover:bg-gold-dark transition-colors duration-300 px-10 py-5 uppercase tracking-widest text-sm font-semibold group"
                data-testid="hero-cta"
              >
                <span>{t('hero.cta')}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-gold-main rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Company Overview Section */}
      <section className="section-padding bg-white" data-testid="overview-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="label-text block mb-4">{t('overview.label')}</span>
              <h2 className="font-alatsi text-4xl md:text-5xl text-navy-deep mb-6">
                {t('overview.title')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {t('overview.description')}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center space-x-2 text-navy-deep hover:text-gold-main transition-colors font-semibold group"
                data-testid="overview-link"
              >
                <span>{t('overview.learnMore')}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div 
              className="grid grid-cols-2 gap-4"
              {...fadeUp}
            >
              <img 
                src={WORK_IMAGE_3}
                alt="Construction drilling work"
                className="w-full h-64 object-cover"
              />
              <img 
                src={WORK_IMAGE_4}
                alt="Metal structure installation"
                className="w-full h-64 object-cover mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="section-padding bg-slate-50" data-testid="services-preview-section">
        <div className="container-main">
          <motion.div 
            className="text-center mb-16"
            {...fadeUp}
          >
            <span className="label-text block mb-4">{t('services.label')}</span>
            <h2 className="font-alatsi text-4xl md:text-5xl text-navy-deep">
              {t('services.title')}
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="group relative bg-white border border-gray-100 p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
                data-testid={`service-card-${index}`}
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-gold-main transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top" />
                <service.icon className="w-12 h-12 text-gold-main mb-6" strokeWidth={1.5} />
                <h3 className="font-alatsi text-xl text-navy-deep mb-3">{service.title}</h3>
                <Link 
                  to={service.link}
                  className="inline-flex items-center text-sm text-gold-main hover:text-navy-deep transition-colors"
                >
                  <span className="mr-2">{t('overview.learnMore')}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="text-center mt-12"
            {...fadeUp}
          >
            <Link
              to="/services"
              className="inline-flex items-center space-x-3 bg-navy-deep text-white hover:bg-gold-main transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-sm font-semibold"
              data-testid="services-view-all"
            >
              <span>{t('services.viewAll')}</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* UK Coverage Map Section */}
      <section className="section-padding bg-white" data-testid="coverage-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="label-text block mb-4">{t('coverage.label')}</span>
              <h2 className="font-alatsi text-4xl md:text-5xl text-navy-deep mb-6">
                {t('coverage.title')}
              </h2>
              <p className="text-2xl text-gold-main font-semibold mb-4">
                {t('coverage.description')}
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {t('coverage.subtitle')}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 border-2 border-navy-deep text-navy-deep hover:bg-navy-deep hover:text-white transition-colors duration-300 px-8 py-4 uppercase tracking-widest text-sm font-semibold"
                data-testid="coverage-cta"
              >
                <span>{t('nav.contact')}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div 
              className="relative"
              {...fadeUp}
            >
              <div className="bg-slate-50 p-8 rounded-sm">
                <UKMap className="max-w-md mx-auto" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-navy-deep" data-testid="why-us-section">
        <div className="container-main">
          <motion.div 
            className="text-center mb-16"
            {...fadeUp}
          >
            <span className="text-gold-main text-sm font-semibold uppercase tracking-widest block mb-4">
              {t('whyUs.label')}
            </span>
            <h2 className="font-alatsi text-4xl md:text-5xl text-white">
              {t('whyUs.title')}
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {whyUsItems.map((item, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="text-center p-6"
                data-testid={`why-us-card-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-main/10 mb-6">
                  <item.icon className="w-8 h-8 text-gold-main" strokeWidth={1.5} />
                </div>
                <h3 className="font-alatsi text-xl text-white mb-3">{item.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gold-main" data-testid="cta-section">
        <div className="container-main text-center">
          <motion.h2 
            className="font-alatsi text-3xl md:text-4xl text-navy-deep mb-8"
            {...fadeUp}
          >
            {t('hero.title')}
          </motion.h2>
          <motion.div {...fadeUp}>
            <Link
              to="/get-in-touch"
              className="inline-flex items-center space-x-3 bg-navy-deep text-white hover:bg-navy-light transition-colors duration-300 px-10 py-5 uppercase tracking-widest text-sm font-semibold"
              data-testid="final-cta"
            >
              <span>{t('hero.cta')}</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
